import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  Paper,
  useTheme,
  CircularProgress,
} from "@mui/material";
import SetupStepHelp from "./setup/SetupStepHelp";
import {
  MAX_SUBTYPE_DEPTH,
  addRequestType,
  addRootSubtype,
  addSubtypeChild,
  removeRequestType,
  removeSubtypeAt,
  setRequestTypeName,
  setSubtypeName,
} from "../utils/setupRequestTypeTree";

function SubtypeTree({
  nodes,
  path,
  depth,
  tipoIndex,
  requestTypes,
  onChange,
  t,
  theme,
}) {
  const list = Array.isArray(nodes) ? nodes : [];
  return (
    <Box>
      {list.map((node, idx) => {
        const here = [...path, idx];
        const labelPath = here.map((n) => n + 1).join(".");
        const canNest = depth < MAX_SUBTYPE_DEPTH;
        const canRemove = !(depth === 1 && list.length <= 1);
        return (
          <Box
            key={here.join("-")}
            sx={{
              mb: 1.25,
              pl: depth > 1 ? 2 : 0,
              borderLeft: depth > 1 ? `2px solid ${theme.palette.divider}` : "none",
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("setup.requestsStep.subtypeLabel", { n: labelPath })}
                  value={node.nome || ""}
                  onChange={(e) => onChange(setSubtypeName(requestTypes, tipoIndex, here, e.target.value))}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { sm: "flex-end" } }}>
                  {canNest ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onChange(addSubtypeChild(requestTypes, tipoIndex, here))}
                    >
                      {t("setup.requestsStep.addNested")}
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => onChange(removeSubtypeAt(requestTypes, tipoIndex, here))}
                    disabled={!canRemove}
                  >
                    {t("setup.requestsStep.remove")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
            {Array.isArray(node.subtipos) && node.subtipos.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                <SubtypeTree
                  nodes={node.subtipos}
                  path={here}
                  depth={depth + 1}
                  tipoIndex={tipoIndex}
                  requestTypes={requestTypes}
                  onChange={onChange}
                  t={t}
                  theme={theme}
                />
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

const EtapaSolicitacoes = ({
  requestTypes,
  onChange,
  onGenerateRequestTypes,
  isGenerating = false,
  errorMessage,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("onboarding");
  useEffect(() => {
    void ensureNamespaceLoaded("onboarding");
  }, []);

  const types = Array.isArray(requestTypes) ? requestTypes : [];

  return (
    <Box sx={{ width: "100%" }}>
      <SetupStepHelp stepKey="requests" />
      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: theme.palette.primary.main }}>
        {t("setup.requestsStep.title")}
      </Typography>

      <Typography variant="body1" gutterBottom>
        {t("setup.requestsStep.intro")}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("setup.requestsStep.hint")}
      </Typography>

      {errorMessage && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.warning.light}`, backgroundColor: theme.palette.warning.light + "22" }}>
          <Typography variant="body2" color="warning.main">
            {errorMessage}
          </Typography>
        </Box>
      )}

      <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={isGenerating || !onGenerateRequestTypes}
          onClick={onGenerateRequestTypes}
        >
          {isGenerating ? <CircularProgress size={24} /> : t("setup.requestsStep.generateAi")}
        </Button>
        {types.length === 0 ? (
          <Button variant="outlined" onClick={() => onChange(addRequestType(types))}>
            {t("setup.requestsStep.addManual")}
          </Button>
        ) : null}
      </Box>

      {types.map((request, tipoIndex) => (
        <Paper key={tipoIndex} sx={{ mb: 3, p: 2, borderLeft: `5px solid ${theme.palette.primary.main}` }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label={t("setup.requestsStep.rootLabel")}
                value={request.tipo}
                onChange={(e) => onChange(setRequestTypeName(types, tipoIndex, e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={4} textAlign="right">
              <Button
                variant="outlined"
                color="error"
                onClick={() => onChange(removeRequestType(types, tipoIndex))}
                disabled={types.length <= 1}
              >
                {t("setup.requestsStep.remove")}
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {t("setup.requestsStep.subtypesHeading")}
          </Typography>

          <SubtypeTree
            nodes={request.subtipos}
            path={[]}
            depth={1}
            tipoIndex={tipoIndex}
            requestTypes={types}
            onChange={onChange}
            t={t}
            theme={theme}
          />

          <Button variant="outlined" onClick={() => onChange(addRootSubtype(types, tipoIndex))} sx={{ mt: 1 }}>
            {t("setup.requestsStep.addSubtype")}
          </Button>
        </Paper>
      ))}

      {types.length > 0 && (
        <Box mt={2}>
          <Button variant="contained" onClick={() => onChange(addRequestType(types))}>
            {t("setup.requestsStep.addType")}
          </Button>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: "block" }}>
        {t("setup.requestsStep.footer")}
      </Typography>
    </Box>
  );
};

export default EtapaSolicitacoes;
